<template>
  <v-dialog persistent v-model="dialog" max-width="640px">
    <PreviousPurchase
      @add="addPreviousPurchase"
      :PurchaseIMEIResult="PurchaseIMEIResult"
      @close="purchaseDialogClose"
      :purchaseDialog="purchaseDialog"
    />
    <v-card class="imei-dialog-card">
      <div class="dialog-header">
        <div class="dialog-heading">
          <div class="dialog-title">Scan IMEI Numbers</div>
          <div class="dialog-subtitle">{{ getSelectedProduct.name }}</div>
        </div>

        <div class="dialog-header-actions">
          <div class="scan-count">{{ serialNumberList.length }} of {{ qty }}</div>
          <v-btn icon small aria-label="Close IMEI dialog" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-progress-linear
        class="scan-progress"
        background-color="blue-grey lighten-5"
        color="primary"
        height="4"
        v-model="reOrderPercentage"
      />

      <v-card-text class="dialog-content">
        <div class="scan-section">
          <div class="section-label">
            {{ bulkEntry ? "Paste IMEI numbers" : "Scan or enter IMEI" }}
          </div>
          <div class="scan-entry-row">
            <v-textarea
              v-if="bulkEntry"
              class="scan-input"
              outlined
              rows="3"
              hide-details="auto"
              placeholder="One IMEI per line"
              v-model="bulkSerialNumber"
            ></v-textarea>
            <v-text-field
              v-else
              class="scan-input"
              id="serialNumberBox"
              @keyup.enter.native="enterSerial()"
              :loading="serailLoading"
              ref="serialNumber"
              clearable
              outlined
              :disabled="serailLoading"
              :counter="length"
              autofocus
              hide-details="auto"
              placeholder="Scan IMEI or type it here"
              @focus="$event.target.select()"
              v-model.trim="serialNumber"
            ></v-text-field>

            <v-btn
              class="add-imei-button"
              color="primary"
              depressed
              :loading="serailLoading"
              :disabled="serailLoading || (!serialNumber && !bulkSerialNumber)"
              @click="enterSerial"
            >
              <v-icon left>mdi-barcode-scan</v-icon>
              Add
            </v-btn>
          </div>
        </div>

        <div class="serial-list-header">
          <div>
            <div class="section-label mb-0">Scanned IMEIs</div>
            <div class="section-hint">Review the list before saving.</div>
          </div>
          <v-btn
            v-if="serialNumberList.length"
            text
            small
            color="error"
            @click="clearAll"
          >
            Clear all
          </v-btn>
        </div>

        <div v-if="!serialNumberList.length" class="empty-serial-list">
          <v-icon color="blue-grey lighten-2" size="34">mdi-barcode-scan</v-icon>
          <div class="empty-title">No IMEIs scanned yet</div>
          <div class="empty-copy">Scan the first device to begin.</div>
        </div>

        <v-list v-else class="serial-list">
          <template v-for="(row, index) in serialNumberList">
            <v-list-item :key="`${row}-${index}`" class="serial-list-item">
              <v-list-item-avatar class="serial-index">
                {{ index + 1 }}
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-if="row">
                  <span
                    class="serial-value"
                    :class="{ 'error--text': row.length != length }"
                    >{{ row }}</span
                  >
                </v-list-item-title>
                <v-list-item-subtitle v-if="row.length != length" class="error--text">
                  Expected {{ length }} digits
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn icon small :aria-label="`Remove IMEI ${row}`" @click="deleteNumber(index)">
                  <v-icon color="error">mdi-delete-outline</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="dialog-footer">
        <div class="scan-options">
          <div class="option-field">
            <div class="option-label">IMEI length</div>
            <v-text-field
              class="right-input"
              @focus="$event.target.select()"
              :prepend-inner-icon="
                lock ? 'mdi-lock-outline' : 'mdi-lock-open-variant-outline'
              "
              @click:prepend-inner="lockStatus"
              v-model="length"
              outlined
              hide-details
              dense
            ></v-text-field>
          </div>
          <div class="option-field">
            <div class="option-label">Unit cost</div>
            <v-text-field
              class="right-input"
              @focus="$event.target.select()"
              prefix="$"
              v-model="cost"
              outlined
              hide-details
              dense
            ></v-text-field>
          </div>
          <v-switch
            class="bulk-switch"
            v-model="bulkEntry"
            label="Bulk entry"
            hide-details
          ></v-switch>
        </div>

        <div class="dialog-actions">
          <v-btn text @click="close">Cancel</v-btn>
          <v-btn
            v-if="getEditImeiNumberList.type === 'new'"
            color="primary"
            depressed
            :disabled="serialNumberList.length === 0"
            @click="saveSerialRows"
          >
            Save IMEIs
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
    <LengthExceeded
      @close="lengthExceeded = false"
      :lengthExceeded="lengthExceeded"
      :serialNumber="serialNumber"
    />
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import purchaseService from "../service";
import PreviousPurchase from "@/modules/Inventory/Purchase/components/PreviousPurchase.vue";
import LengthExceeded from "@/modules/Inventory/Purchase/components/LengthExceeded.vue";

export default {
  name: "collect-imeinumber",
  data() {
    return {
      currentIndex: 0,
      bulkEntry: false,
      bulkSerialNumber: null,
      bulkSerialNumberList: [],
      lengthExceeded: false,
      lock: true,
      PurchaseIMEIResult: null,
      purchaseDialog: false,
      dialog: false,
      serailLoading: false,
      serialNumber: null,
      length: 15,
      cost: 0,
      qty: 0,
      serialNumberList: [],
      pendingSerialNumbers: [],
      reOrderPercentage: 0,
    };
  },
  components: {
    PreviousPurchase,
    LengthExceeded,
  },
  computed: {
    ...mapGetters("purchase", [
      "getImeiNumberDialog",
      "getEditImeiNumberList",
      "PurchaseDetails",
      "getSelectedProduct",
      "getEditImeiNumberList",
    ]),
    title() {
      return `${this.serialNumberList.length} / ${this.qty} -  ${this.getSelectedProduct.name}`;
    },
  },
  watch: {
    bulkEntry() {
      this.serialNumber = null;
    },
    // dialog(val) {
    //   if (val) {
    //     this.serialNumberList = null;
    //   }
    // },
    serialNumberList(val) {
      if (val.length == this.qty) {
        if (!this.bulkEntry) {
          this.saveSerialRows();
        }
      }
    },
    getImeiNumberDialog(val) {
      this.dialog = val;
      if (val) {
        if (this.getEditImeiNumberList.type === "new") {
          this.resetDialogState();
          this.cost = this.getSelectedProduct.Bar.cost;
          this.qty = this.getSelectedProduct.Bar.qty;
        } else {
          this.loadNumbers();
        }
      }
    },
  },
  methods: {
    ...mapActions("purchase", [
      "AddPurchaseDetail",
      "SetImeiNumberDialog",
      "UpdatePurchaseDetail",
    ]),
    clearAll() {
      this.$swal({
        title: "Are you sure?",
        text: `You have ${this.serialNumberList.length} serial in this list. You want to remove them?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          this.serialNumberList = [];
        }
      });
    },
    closeLengthDialog() {
      this.lengthExceeded = false;
      setTimeout(function () {
        if (this.$refs && this.$refs.serialNumber)
          this.$refs.serialNumber.focus();
      }, 300);
    },
    lockStatus() {
      this.lock = !this.lock;
    },
    viewPrucaseDialog() {
      const url = `/global/purchase/${this.PurchaseIMEIResult.Purchase.id}/view`;

      const routeData = this.$router.resolve({
        path: url,
      });
      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },
    deleteNumber(i) {
      this.serialNumberList.splice(i, 1);
    },
    loadNumbers() {
      const index = this.getEditImeiNumberList.index;
      this.cost = parseFloat(this.PurchaseDetails[index].cost);
      this.serialNumberList = this.PurchaseDetails[index].imeiNumbers;
    },
    resetDialogState() {
      this.serialNumber = null;
      this.bulkSerialNumber = null;
      this.bulkSerialNumberList = [];
      this.bulkEntry = false;
      this.serialNumberList = [];
      this.pendingSerialNumbers = [];
      this.PurchaseIMEIResult = null;
      this.purchaseDialog = false;
      this.serailLoading = false;
      this.reOrderPercentage = 0;
      this.currentIndex = 0;
      this.qty = 0;
      this.cost = 0;
    },
    forceClose() {
      this.SetImeiNumberDialog(false);
      this.$emit("close");
    },
    async close() {
      if (this.serialNumberList.length) {
        const result = await this.$swal({
          title: "Discard scanned IMEIs?",
          text: `All ${this.serialNumberList.length} unsaved IMEI ${
            this.serialNumberList.length === 1 ? "number" : "numbers"
          } will be deleted.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, discard",
          cancelButtonText: "Keep editing",
          confirmButtonColor: "#dc2626",
        });

        if (!result.value) return;
      }

      this.resetDialogState();
      this.forceClose();
    },
    emptyEntryBar() {
      this.serialNumberList = [];
      this.cost = 0;
      this.reOrderPercentage = 0;
      setTimeout(function () {
        if (this.$refs && this.$refs.serialNumber)
          this.$refs.serialNumber.focus();
      }, 300);
    },
    // async updateSerialRows() {
    //   await this.UpdatePurchaseDetail({
    //     index: this.getEditImeiNumberList.index,
    //     imeiNumbers: this.serialNumberList,
    //     qty: this.serialNumberList.length,
    //     cost: this.cost,
    //     total: this.serialNumberList.length * this.cost,
    //   });

    //   this.emptyEntryBar();
    //   this.dialog = false;
    // },
    purchaseDialogClose() {
      this.serialNumber = null;
      this.purchaseDialog = false;
      this.bulkEntry = false;
      this.bulkSerialNumber = null;
      this.bulkSerialNumberList = [];
      setTimeout(() => {
        this.$refs.serialNumber.focus();
      }, 100);
    },
    async saveSerialRows() {
      // // console.log("saveSerialRows", this.serialNumberList.length);

      for (let index = 0; index < this.serialNumberList.length; index++) {
        const x = this.serialNumberList[index];
        const row = {
          ProductId: this.getSelectedProduct.id,
          name: this.getSelectedProduct.name,
          imeiNumber: x,
          cost: this.cost,
          total: this.cost,
          qty: 1,
          Product: this.getSelectedProduct,
          metadata: {
            serialStatus: this.getSelectedProduct.Bar.status,
          },
        };
        // console.log("row adding", index, this.serialNumberList.length);
        this.AddPurchaseDetail(row);
        if (index >= this.serialNumberList.length - 1) {
          this.emptyEntryBar();
          this.forceClose();
        }
      }
      // await Promise.all(this.serialNumberList.map((x) => {
      //   const row = {
      //     ProductId: this.getSelectedProduct.id,
      //     name: this.getSelectedProduct.name,
      //     imeiNumber: x,
      //     cost: this.cost,
      //     total: this.cost,
      //     qty: 1,
      //     Product: this.getSelectedProduct,
      //   };
      //   // console.log("row adding", row);
      //   this.AddPurchaseDetail(row);
      // }))
    },
    async enterSerial() {
      if (this.bulkEntry) {
        if (!this.bulkSerialNumber) return false;
        this.bulkSerialNumberList = this.bulkSerialNumber.split("\n");
        const seen = new Set();
        const uniqueSerials = this.bulkSerialNumberList
          .map((value) => value.trim())
          .filter((value) => {
            const normalized = value.toUpperCase();
            if (!normalized || seen.has(normalized)) return false;
            seen.add(normalized);
            return true;
          });

        for (let index = 0; index < uniqueSerials.length; index++) {
          const element = uniqueSerials[index];
          this.currentIndex = index;
          this.serialNumber = element;
          await this.addSerial(element);
        }
        this.bulkSerialNumber = null;
      } else {
        await this.addSerial(this.serialNumber);
      }
    },
    async addSerial(sNumber) {
      if (!sNumber) return false;
      sNumber = String(sNumber).trim();
      const normalizedSerial = sNumber.toUpperCase();
      if (this.lock && sNumber.length !== parseInt(this.length)) {
        this.lengthExceeded = true;
        return false;
      }

      // // console.log("serialNumber", sNumber);

      let tempSerialNumberList = [];

      tempSerialNumberList = this.serialNumberList;

      let index = tempSerialNumberList.findIndex(
        (value) => String(value).trim().toUpperCase() === normalizedSerial
      );

      const imeiResult = this.PurchaseDetails.find((row) => {
        if (
          !row.delete &&
          String(row.imeiNumber || "").trim().toUpperCase() === normalizedSerial
        ) {
          return true;
        }
      });

      if (
        imeiResult ||
        index != -1 ||
        this.pendingSerialNumbers.includes(normalizedSerial)
      ) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 4000,
          })
          .fire({
            icon: "error",
            title: `${sNumber} is already in this list`,
          });
        const input = document.getElementById("serialNumberBox");
        if (input) {
          input.select();
        }

        // this.$swal({
        //   title: "Already exist!",
        //   text: `${this.serialNumber} is already in this list`,
        //   icon: "warning",
        // });
        if (!this.bulkEntry) {
          this.$refs.serialNumber.focus();
        }
      } else {
        this.pendingSerialNumbers.push(normalizedSerial);
        this.serailLoading = true;
        try {
          this.PurchaseIMEIResult = (
            await purchaseService.search({
              imeiNumber: sNumber,
              ignoreStatus: true,
            })
          ).data;

          // console.log("imeiResult", this.PurchaseIMEIResult, this.bulkEntry);
          if (this.bulkEntry) {
            this.addPreviousPurchase(sNumber);
          } else {
            if (this.PurchaseIMEIResult) {
              this.purchaseDialog = true;
            } else {
              // console.log("add previous purchase in IMEI");
              this.addPreviousPurchase(sNumber);
            }
          }

        } catch (error) {
          console.log("error", error);
        } finally {
          this.pendingSerialNumbers = this.pendingSerialNumbers.filter(
            (serial) => serial !== normalizedSerial
          );
          this.serailLoading = false;
        }
      }
    },
    addPreviousPurchase(sNumber) {
      // console.log("serialNumberList", sNumber, this.serialNumber, this.bulkEntry);
      if (!sNumber) sNumber = this.serialNumber;

      const normalizedSerial = String(sNumber).trim().toUpperCase();
      const alreadyAdded = this.serialNumberList.some(
        (value) => String(value).trim().toUpperCase() === normalizedSerial
      );
      if (alreadyAdded) return false;

      this.serialNumberList.unshift(String(sNumber).trim());
      this.reOrderPercentage =
        (this.serialNumberList.length / this.getSelectedProduct.Bar.qty) * 100;
      this.serialNumber = null;

      if (!this.bulkEntry) {
        this.$refs.serialNumber.focus();
        this.purchaseDialogClose();
      }

      // if (this.PurchaseIMEIResult[0].qty > 0) {

      // }
    },
  },
};
</script>

<style scoped>
.imei-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  min-height: 78px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid #e2e8f0;
}

.dialog-heading {
  min-width: 0;
}

.dialog-title {
  color: #0f172a;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.2;
}

.dialog-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dialog-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.scan-count {
  padding: 6px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.scan-progress {
  margin: 0;
}

.dialog-content {
  padding: 20px !important;
}

.scan-section {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  background: #f8fafc;
}

.section-label,
.option-label {
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
}

.section-hint {
  margin-top: 3px;
  color: #64748b;
  font-size: 13px;
}

.scan-entry-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.scan-input {
  flex: 1 1 auto;
}

.add-imei-button {
  min-height: 56px;
  padding: 0 20px !important;
}

.serial-list-header {
  margin: 22px 2px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.empty-serial-list {
  min-height: 170px;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
  text-align: center;
}

.empty-title {
  margin-top: 10px;
  color: #334155;
  font-size: 15px;
  font-weight: 800;
}

.empty-copy {
  margin-top: 3px;
  font-size: 13px;
}

.serial-list {
  max-height: 260px;
  padding: 0;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.serial-list-item + .serial-list-item {
  border-top: 1px solid #eef2f7;
}

.serial-index {
  min-width: 32px !important;
  width: 32px !important;
  height: 32px !important;
  margin-right: 12px !important;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 900;
}

.serial-value {
  color: #0f172a;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.dialog-footer {
  padding: 16px 20px !important;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.scan-options,
.dialog-actions {
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.option-field {
  width: 112px;
}

.bulk-switch {
  min-width: 108px;
  margin: 0 0 8px;
}

.bulk-switch ::v-deep .v-label {
  white-space: nowrap;
}

@media (max-width: 600px) {
  .dialog-header,
  .dialog-content,
  .dialog-footer {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .scan-entry-row,
  .dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .add-imei-button {
    width: 100%;
  }

  .scan-options,
  .dialog-actions {
    width: 100%;
  }

  .scan-options {
    align-items: center;
  }

  .option-field {
    flex: 1 1 0;
    width: auto;
  }

  .dialog-actions {
    justify-content: flex-end;
  }
}
</style>
